/** Store-scoped metadata catalog, persistent preferences and selected relationship details. */
import { useState } from 'react'
import type { ReactNode } from 'react'
import type { StoreProjectSource } from '@testfeed/integration-contracts'
import { productLanguage, shopifyComponentCopy } from '@testfeed/product-language'
import type { ShopifyTokenSource } from '../shopifyApiFetch'
import { resolveMerchantErrorMessage } from '../merchantErrors'
import { ShopifyPageSkeleton } from './ShopifyPageSkeleton'
import { FooterHelp, HelpModal } from './shared/PageHelp'
import { ExistingPropertyPanel } from './integrations/ExistingPropertyPanel'
import { IntegrationProviderCard } from './integrations/IntegrationProviderCard'
import { IntegrationSourceDetails } from './integrations/IntegrationSourceDetails'
import { useIntegrationsPage } from './integrations/useIntegrationsPage'
const copy = shopifyComponentCopy.integrationsPage
const oldCopy = productLanguage.shopify.integrations
const HELP_MODAL = 'integrations-help-modal'
type Props = {
  shopify: ShopifyTokenSource
  isTestFeedConnected: boolean
  connectionId?: string | null
  shopDomain?: string | null
  showFooterHelp?: boolean
  onConnectGoogle?: () => void
}

export function ShopifyIntegrationsPage(props: Props) {
  const page = useIntegrationsPage(props.shopify, props.isTestFeedConnected)
  return <IntegrationPageScope key={page.scopeKey} {...props} page={page} />
}
function IntegrationPageScope(props: Props & { page: ReturnType<typeof useIntegrationsPage> }) {
  const { page } = props
  const [showProperties, setShowProperties] = useState(false)
  const [selected, setSelected] = useState<StoreProjectSource | null>(null)
  if (!props.isTestFeedConnected)
    return (
      <s-banner heading={oldCopy.disconnected.title} tone="warning">
        <s-text>{oldCopy.disconnected.body}</s-text>
      </s-banner>
    )
  if (page.catalog.isLoading) return <ShopifyPageSkeleton accessibilityLabel={oldCopy.loading} />
  let error: ReactNode = null
  if (page.catalog.error)
    error = (
      <s-banner heading={oldCopy.loadFailedTitle} tone="critical">
        {resolveMerchantErrorMessage(page.catalog.error, oldCopy.loadFailedFallback)}
      </s-banner>
    )
  if (page.catalog.error && !page.catalog.data) return error
  const canChoose = page.capabilities.includes('existing-properties')
  const canReadStatus = page.capabilities.includes('source-status')
  const canVote = page.capabilities.includes('votes') && !!page.votes.data && !page.votes.error
  const canConnect = page.capabilities.includes('google-setup') && !!props.onConnectGoogle
  const sources = page.catalog.data?.projectSources ?? []
  const providers = page.catalog.data?.providers ?? []
  const groups = [
    { title: copy.available, rows: providers.filter((row) => row.availability === 'enabled') },
    { title: copy.comingSoon, rows: providers.filter((row) => row.availability === 'coming_soon') },
  ]
  let properties = null
  if (showProperties && canChoose)
    properties = (
      <ExistingPropertyPanel
        shopify={props.shopify}
        onClose={() => setShowProperties(false)}
        onSaved={() => {
          void page.refresh()
          setShowProperties(false)
        }}
      />
    )
  let details = null
  if (selected && canReadStatus)
    details = (
      <IntegrationSourceDetails
        key={selected.relationshipId}
        source={selected}
        shopify={props.shopify}
        canUnlink={canChoose}
        onClose={() => setSelected(null)}
        onSaved={() => {
          void page.refresh()
          setSelected(null)
        }}
      />
    )
  let voteFailure = null
  if (page.vote.error || page.votes.error) voteFailure = <s-banner tone="critical">{copy.votesFailed}</s-banner>
  let help = null
  if (props.showFooterHelp !== false)
    help = (
      <>
        <FooterHelp modalId={HELP_MODAL} link={copy.helpLink} />
        <HelpModal id={HELP_MODAL} heading={copy.helpHeading}>
          <s-paragraph>{copy.helpBody}</s-paragraph>
          <s-paragraph color="subdued">{copy.comingSoonHelp}</s-paragraph>
        </HelpModal>
      </>
    )
  return (
    <s-stack gap="large-200">
      {error}
      {voteFailure}
      {groups
        .filter((group) => group.rows.length > 0)
        .map((group) => (
          <s-stack key={group.title} gap="base">
            <s-heading>{group.title}</s-heading>
            {group.rows.map((provider) => {
              const voted = page.votes.data?.votes.some((vote) => vote.provider === provider.id && vote.voted) === true
              let choose: (() => void) | undefined
              let connect: (() => void) | undefined
              if (canChoose) choose = () => setShowProperties(true)
              if (canConnect) connect = props.onConnectGoogle
              let providerSources: StoreProjectSource[] = []
              if (provider.id === 'google-search-console') providerSources = sources
              let showDetails: ((source: StoreProjectSource) => void) | undefined
              if (canReadStatus) showDetails = setSelected
              return (
                <IntegrationProviderCard
                  key={provider.id}
                  provider={provider}
                  sources={providerSources}
                  voted={voted}
                  voteAvailable={canVote}
                  votePending={page.vote.isPending}
                  onVote={() => page.vote.mutate({ provider: provider.id, voted: !voted })}
                  onChooseProperty={choose}
                  onConnectGoogle={connect}
                  onDetails={showDetails}
                  googleSetupUnavailable={!canConnect && canChoose}
                />
              )
            })}
          </s-stack>
        ))}
      <s-section heading={copy.organizationAccess}>
        <s-text>{copy.organizationAccessExplanation}</s-text>
        <s-link href={page.catalog.data?.testFeedIntegrationsUrl} target="_blank">
          {copy.manageOrganization}
        </s-link>
      </s-section>
      {properties}
      {details}
      {help}
    </s-stack>
  )
}
